import { Controller } from '@hotwired/stimulus';

// Browser → USB thermal printer, ESC/POS, no print dialog.
//
// The server builds the receipt (Receipt, and the host's presenter behind
// its receipt route); this only carries bytes.
// WebUSB grants stick to the origin, so pairing is a one-time gesture: after
// it, getDevices() finds the printer again and every receipt is silent.
//
// Chrome and Edge only. Safari and Firefox — and any box where the OS holds
// the printer (Windows usbprint) — fall through to the print dialog with the
// text receipt. See fire/docs/POS.md.
export default class extends Controller {
  static targets = ['pair', 'ready', 'none', 'width', 'raster'];
  static values = { auto: String }; // receipt urls, space apart, to print the moment we land

  async connect() {
    await this.paint();
    // The door and the counter press nothing: a sale that just happened
    // prints itself, but only on a box that already has a printer.
    // One paper per url, in order: a printer's buffer takes one receipt at a time.
    if (this.device) for (const url of this.autoValue.split(/\s+/).filter(Boolean)) await this.send(url);
  }

  get usb() {
    return navigator.usb;
  }

  // What the paired printer is: 80mm/58mm paper, and whether its firmware
  // can draw a QR. Lives in the browser because it describes this counter.
  get setup() {
    return {
      w: localStorage.getItem('printer:w') || '48',
      qr: localStorage.getItem('printer:qr') || 'native',
    };
  }

  save() {
    if (this.hasWidthTarget) localStorage.setItem('printer:w', this.widthTarget.value);
    if (this.hasRasterTarget) localStorage.setItem('printer:qr', this.rasterTarget.checked ? 'raster' : 'native');
  }

  async pair() {
    try {
      this.device = await this.usb.requestDevice({
        filters: [{ classCode: 7 }, { classCode: 0xff }],
      });
      await this.paint();
    } catch {
      // The chooser was dismissed — nothing to say, nothing paired.
    }
  }

  async print({ params: { url } }) {
    await this.send(url);
  }

  async send(url) {
    const { w, qr } = this.setup;
    // A url may already carry its own question (?out=1 is the cash desk's).
    const src = `${url}${url.includes('?') ? '&' : '?'}w=${w}&qr=${qr}`;
    if (!this.device) return this.dialog(src);

    try {
      const res = await fetch(src);
      const bytes = new Uint8Array(await res.arrayBuffer());
      await this.write(bytes);
    } catch (e) {
      console.error('print failed', e);
      this.device = null;
      this.dialog(src); // paper, one way or another
    }
  }

  // Claim, write in chunks a small printer buffer can swallow, let go.
  async write(bytes) {
    const d = this.device;
    await d.open();
    if (!d.configuration) await d.selectConfiguration(1);
    const iface = d.configuration.interfaces.find((i) => i.alternate.endpoints.some((e) => e.direction === 'out'));
    await d.claimInterface(iface.interfaceNumber);
    const out = iface.alternate.endpoints.find((e) => e.direction === 'out');

    for (let i = 0; i < bytes.length; i += 4096) {
      await d.transferOut(out.endpointNumber, bytes.slice(i, i + 4096));
    }
    await d.releaseInterface(iface.interfaceNumber);
  }

  // Last rung of the ladder: the text receipt in the OS print dialog.
  async dialog(url) {
    const res = await fetch(`${url}&format=txt`, { headers: { Accept: 'text/plain' } });
    const frame = document.createElement('iframe');
    frame.style.display = 'none';
    document.body.appendChild(frame);
    const text = (await res.text()).replace(/[<&]/g, (c) => (c === '<' ? '&lt;' : '&amp;'));
    frame.contentDocument.write(`<pre style="font:12px monospace">${text}</pre>`);
    frame.contentDocument.close();
    frame.contentWindow.print();
    setTimeout(() => frame.remove(), 1000);
  }

  async paint() {
    if (!this.usb) return this.show('none');
    [this.device] = await this.usb.getDevices();
    this.show(this.device ? 'ready' : 'pair');
    if (this.hasWidthTarget) this.widthTarget.value = this.setup.w;
    if (this.hasRasterTarget) this.rasterTarget.checked = this.setup.qr === 'raster';
  }

  show(state) {
    if (this.hasPairTarget) this.pairTarget.classList.toggle('hidden', state !== 'pair');
    if (this.hasReadyTarget) this.readyTarget.classList.toggle('hidden', state !== 'ready');
    if (this.hasNoneTarget) this.noneTarget.classList.toggle('hidden', state !== 'none');
  }
}
